import { RendererTypes } from "@bronte/viewer-api/src/main";
import { JsIntegrations as ExpressJsIntegrations } from "../../../viewer-express-api/src/JsIntegrations";
import { createIntegrationsFunc } from "./JSIntegrationsData";
import {
  DEFAULT_ETEXT_WIDGET_TEST_CONFIG,
  normalizeEtextWidgetTestConfig,
  type EtextWidgetTestConfig,
} from "./etextWidgetTestConfig";

const WEB_WIDGET_LIB_SCRIPT_ID = "bronte-aet-widget-lib";
const WEB_WIDGET_LAYER_ID = "dev-shell-aet-widget-layer";
const WEB_WIDGET_MOUNT_SELECTOR = ".content-frame-container";

type WidgetWindow = Window & {
  renderFeedbackWidget?: (...args: unknown[]) => void;
  renderMCQWidget?: (...args: unknown[]) => void;
};

const isWidgetLibraryReady = (widgetWindow: WidgetWindow): boolean =>
  Boolean(widgetWindow.renderFeedbackWidget || widgetWindow.renderMCQWidget);

let webWidgetGlobalStylesRemoved = false;

const isWidgetStyleElement = (styleEl: HTMLStyleElement): boolean => {
  const css = styleEl.textContent ?? "";
  return css.includes("feed-back-main-container") || css.includes("--font-family-primary");
};

/** widget.js global CSS collides with dev-shell (e.g. `.content-wrapper`, `.header`). */
const removeWebWidgetGlobalStylesOnce = (): void => {
  if (webWidgetGlobalStylesRemoved) {
    return;
  }

  for (const styleEl of Array.from(document.head.querySelectorAll("style"))) {
    if (!(styleEl instanceof HTMLStyleElement) || !isWidgetStyleElement(styleEl)) {
      continue;
    }
    styleEl.remove();
  }

  webWidgetGlobalStylesRemoved = true;
};

const getOrCreateWebWidgetLayer = (): HTMLElement | null => {
  const mountContainer = document.querySelector(WEB_WIDGET_MOUNT_SELECTOR);
  if (!mountContainer) {
    console.warn("loadTestEtextWidget: .content-frame-container not found; skipping widget mount");
    return null;
  }

  let layer = mountContainer.querySelector(`#${WEB_WIDGET_LAYER_ID}`) as HTMLElement | null;
  if (!layer) {
    layer = document.createElement("div");
    layer.id = WEB_WIDGET_LAYER_ID;
    layer.style.cssText = [
      "position:absolute",
      "left:0",
      "right:0",
      "bottom:0",
      "z-index:10",
      "pointer-events:none",
      "max-height:70vh",
      "overflow:auto",
    ].join(";");
    mountContainer.appendChild(layer);
  }
  return layer;
};

const appendInlineScript = (jsContentInitCommand: string): void => {
  const script = document.createElement("script");
  script.textContent = jsContentInitCommand;
  document.head.append(script);
};

const findFeedbackChatInput = (): HTMLInputElement | null => {
  for (const host of document.querySelectorAll('[data-aet-widget-host="true"]')) {
    const input = host.shadowRoot?.querySelector(".chat-input");
    if (input instanceof HTMLInputElement) {
      return input;
    }
  }
  return null;
};

/** widget.js feedback input always starts empty; dev-shell can pre-fill for QE via `text` in JSON. */
const prefillFeedbackWidgetInput = async (text: string): Promise<void> => {
  const deadline = Date.now() + 5000;
  while (Date.now() < deadline) {
    const input = findFeedbackChatInput();
    if (input) {
      input.value = text;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  console.warn("loadTestEtextWidget: could not pre-fill feedback input (.chat-input not found)");
};

const waitForWidgetLibrary = (timeoutMs = 10000): Promise<boolean> => {
  const widgetWindow = window as WidgetWindow;
  if (isWidgetLibraryReady(widgetWindow)) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = (): void => {
      if (isWidgetLibraryReady(window as WidgetWindow)) {
        resolve(true);
        return;
      }
      if (Date.now() >= deadline) {
        console.error("loadTestEtextWidget: timed out waiting for widget.js");
        resolve(false);
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
};

const ensureWebWidgetLibrary = (jsContent: string[]): Promise<boolean> => {
  if (isWidgetLibraryReady(window as WidgetWindow)) {
    return Promise.resolve(true);
  }

  if (!document.getElementById(WEB_WIDGET_LIB_SCRIPT_ID) && jsContent?.length) {
    const script = document.createElement("script");
    script.textContent = jsContent.join(";");
    script.id = WEB_WIDGET_LIB_SCRIPT_ID;
    document.head.append(script);
    removeWebWidgetGlobalStylesOnce();
  }

  return waitForWidgetLibrary();
};

/**
 * BV (iframe) mounts the widget on `document.body` by element id === screenId.
 * Do not use xpath here — course iframes cover `.content-frame` and hide anchors placed there.
 */
const appendWebInitScript = (screenId: string, jsContentInitCommand: string): void => {
  if (
    jsContentInitCommand.includes("document.body.innerHTML") &&
    jsContentInitCommand.includes("data-id=")
  ) {
    const layer = getOrCreateWebWidgetLayer();
    if (!layer) {
      return;
    }
    const anchor = document.createElement("div");
    anchor.dataset.id = screenId;
    anchor.id = screenId;
    anchor.style.cssText = "display:block;pointer-events:auto;";
    layer.appendChild(anchor);
    return;
  }

  if (/\brender(?:Feedback|MCQ)Widget\s*\(/.test(jsContentInitCommand)) {
    appendInlineScript(jsContentInitCommand);
    return;
  }

  appendInlineScript(jsContentInitCommand);
};

const cleanupWebWidgetHosts = (screenId?: string): void => {
  (window as WidgetWindow & { removeAllWidgets?: () => void }).removeAllWidgets?.();
  document.querySelectorAll('[data-aet-widget-host="true"]').forEach((host) => host.remove());
  document.querySelectorAll('[id^="aet-widget-anchor-"]').forEach((anchor) => anchor.remove());

  if (screenId) {
    // BV anchors live under .content-frame-container (see appendWebInitScript).
    // Do not use document.getElementById — TOC <li>s reuse the same screen ids.
    document
      .querySelector(WEB_WIDGET_MOUNT_SELECTOR)
      ?.querySelector(`[id="${screenId}"]`)
      ?.remove();
  }

  document
    .querySelector(WEB_WIDGET_MOUNT_SELECTOR)
    ?.querySelector(`#${WEB_WIDGET_LAYER_ID}`)
    ?.remove();
};

/**
 * Removes any QE test widget from the page (BV and BVE).
 */
export const closeTestEtextWidget = (screenId?: string): void => {
  cleanupWebWidgetHosts(screenId);
};

const mountWebTestWidget = async (
  screenId: string,
  testConfig: EtextWidgetTestConfig,
  feedbackPrefillText?: string,
): Promise<void> => {
  const config = createIntegrationsFunc(testConfig)(screenId);
  const { jsContent, onScriptLoad } = config;

  const ready = await ensureWebWidgetLibrary(jsContent ?? []);
  if (!ready || !onScriptLoad) {
    return;
  }

  onScriptLoad((jsContentInitCommand: string) => {
    appendWebInitScript(screenId, jsContentInitCommand);
  });

  if (feedbackPrefillText) {
    await prefillFeedbackWidgetInput(feedbackPrefillText);
  }
};

const loadWebTestEtextWidget = async (
  screenId: string,
  testConfig: EtextWidgetTestConfig,
  feedbackPrefillText?: string,
): Promise<void> => {
  cleanupWebWidgetHosts(screenId);
  await mountWebTestWidget(screenId, testConfig, feedbackPrefillText);
};

/**
 * Loads the assignable eText feedback widget (`widget.js`) with mock MCQ data for QE.
 * Works for offline BV (iframe) and BVE (express) dev-shell builds.
 */
export const loadTestEtextWidget = async (
  screenId: string,
  renderer: RendererTypes,
  testConfig: EtextWidgetTestConfig = DEFAULT_ETEXT_WIDGET_TEST_CONFIG,
): Promise<void> => {
  if (!screenId?.trim()) {
    window.alert("Navigate to a screen first, then load the test eText widget.");
    return;
  }

  const { config: normalizedConfig, feedbackPrefillText } =
    normalizeEtextWidgetTestConfig(testConfig);
  const integrationsFunc = createIntegrationsFunc(normalizedConfig);

  if (renderer === RendererTypes.EXPRESS) {
    await ExpressJsIntegrations.fetchDynamicJSScripts(screenId, integrationsFunc, { force: true });
    if (feedbackPrefillText) {
      await prefillFeedbackWidgetInput(feedbackPrefillText);
    }
    return;
  }

  await loadWebTestEtextWidget(screenId, normalizedConfig, feedbackPrefillText);
};
